import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { format } from 'date-fns';
import { parseLanguageCsv } from '../../utils/languageOptions';
import {
  Calendar,
  Clock,
  Users,
  Zap,
  Archive,
  PlayCircle,
  ChevronRight,
  Video,
  Search,
  Play,
} from 'lucide-react';

const STATUS_CONFIG = {
  ONGOING: { label: 'Live Now', color: '#16a34a', bg: '#dcfce7', dot: true },
  SCHEDULED: { label: 'Upcoming', color: '#2563eb', bg: '#dbeafe', dot: false },
  COMPLETED: { label: 'Ended', color: '#64748b', bg: '#f1f5f9', dot: false },
  CANCELLED: { label: 'Cancelled', color: '#dc2626', bg: '#fee2e2', dot: false },
  ARCHIVED: { label: 'Archived', color: '#94a3b8', bg: '#f8fafc', dot: false },
};

const isUpcoming = (meeting) => ['SCHEDULED', 'ONGOING'].includes(meeting.status) && meeting.isActive !== false;
const getLanguageList = (meeting) => parseLanguageCsv(meeting?.language);

const SpeakerMeetings = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [doctorFilter, setDoctorFilter] = useState('');
  const [topicFilter, setTopicFilter] = useState('');
  const [languageFilter, setLanguageFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [joiningId, setJoiningId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMeetings = async () => {
      try {
        const response = await api.get('/meetings');
        setMeetings(response.data || []);
      } catch (error) {
        console.error('Failed to fetch meetings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMeetings();
  }, []);

  const isAssignedSpeaker = (meeting) =>
    meeting.speakers?.some((s) => s.speakerId === user?.id || s.speaker?.id === user?.id);

  const handleJoinMeeting = async (meetingId) => {
    try {
      setJoiningId(meetingId);
      const response = await api.post('/meetings/secure/generate-join-link', { meetingId });

      if (response.data?.joinLink && response.data?.token) {
        sessionStorage.setItem(`meeting_token_${meetingId}`, response.data.token);
        navigate(`/meeting/join?meetingId=${meetingId}&token=${response.data.token}`);
      }
    } catch (error) {
      console.error('Failed to join meeting:', error);
      alert(error.response?.data?.error || 'Failed to generate meeting link');
    } finally {
      setJoiningId(null);
    }
  };

  const handlePlayArchive = (meeting) => {
    if (!meeting.archiveRecordingUrl) {
      return;
    }

    navigate(`/meeting/stream/${meeting.id}`, {
      state: {
        streamEmbedUrl: meeting.archiveRecordingUrl,
        meetingTitle: `${meeting.title} (Recording)`,
      },
    });
  };

  const getSpeakerNames = (meeting) =>
    (meeting.speakers || []).map((s2) => s2.speaker?.name || s2.name).filter(Boolean).join(', ');

  const matchFilters = (meeting) => {
    const q = searchTerm.toLowerCase();
    const dateLabel = format(new Date(meeting.startTime), 'yyyy-MM-dd');
    const topic = (meeting.topic || meeting.title || '').toLowerCase();
    const languages = getLanguageList(meeting).map((item) => item.toLowerCase());
    const doctors = getSpeakerNames(meeting).toLowerCase();

    const matchesSearch =
      !q ||
      meeting.title.toLowerCase().includes(q) ||
      meeting.description?.toLowerCase().includes(q) ||
      topic.includes(q);

    const matchesDoctor = !doctorFilter || doctors.includes(doctorFilter.toLowerCase());
    const matchesTopic = !topicFilter || topic.includes(topicFilter.toLowerCase());
    const matchesLanguage = languageFilter === 'all' || languages.includes(languageFilter.toLowerCase());
    const matchesDate = !dateFilter || dateLabel === dateFilter;

    return matchesSearch && matchesDoctor && matchesTopic && matchesLanguage && matchesDate;
  };

  const availableLanguages = Array.from(
    new Set(meetings.flatMap((meeting) => getLanguageList(meeting)))
  ).sort((a, b) => a.localeCompare(b));

  const filteredMeetings = meetings.filter((meeting) => matchFilters(meeting));

  // Cards: only upcoming/live meetings where this speaker is assigned
  const upcoming = filteredMeetings.filter(
    (meeting) => isUpcoming(meeting) && isAssignedSpeaker(meeting)
  );

  // Table: ALL meetings (including unassigned upcoming + all past), sorted newest first
  const allTableMeetings = filteredMeetings
    .sort((a, b) => new Date(b.startTime) - new Date(a.startTime));

  const StatusPill = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.SCHEDULED;
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px',
          padding: '3px 10px',
          borderRadius: '20px',
          fontSize: '11px',
          fontWeight: '600',
          backgroundColor: cfg.bg,
          color: cfg.color,
        }}
      >
        {cfg.dot && (
          <span
            style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              backgroundColor: cfg.color,
              animation: 'pulse 1.5s infinite',
            }}
          />
        )}
        {cfg.label}
      </span>
    );
  };

  const styles = {
    container: {
      padding: '28px 24px',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    title: {
      margin: 0,
      fontSize: '24px',
      fontWeight: '700',
      color: theme.colors.text,
    },
    subtitle: {
      marginTop: '6px',
      marginBottom: '16px',
      color: theme.colors.textSecondary,
      fontSize: '14px',
    },
    searchWrap: {
      position: 'relative',
      maxWidth: '440px',
      marginBottom: '12px',
    },
    searchIcon: {
      position: 'absolute',
      left: '14px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: theme.colors.textTertiary,
      pointerEvents: 'none',
    },
    searchInput: {
      width: '100%',
      padding: '10px 14px 10px 40px',
      borderRadius: '10px',
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.cardBg,
      color: theme.colors.text,
      fontSize: '14px',
      outline: 'none',
      boxSizing: 'border-box',
    },
    filtersGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '10px',
      marginBottom: '16px',
    },
    filterInput: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '10px',
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.cardBg,
      color: theme.colors.text,
      fontSize: '13px',
      outline: 'none',
      boxSizing: 'border-box',
    },
    sectionLabel: {
      fontSize: '11px',
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: '0.8px',
      color: theme.colors.textTertiary,
      marginBottom: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
      gap: '14px',
      marginBottom: '40px',
    },
    cardBody: {
      padding: '16px',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    badge: (assigned) => ({
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: '999px',
      fontSize: '12px',
      fontWeight: '600',
      backgroundColor: assigned ? `${theme.colors.primary}18` : theme.colors.backgroundSecondary,
      color: assigned ? theme.colors.primary : theme.colors.textSecondary,
      marginBottom: '12px',
    }),
    meetingTitle: {
      margin: '0 0 10px 0',
      color: theme.colors.text,
      fontSize: '15px',
      fontWeight: '600',
      lineHeight: '1.35',
    },
    meta: {
      fontSize: '12px',
      color: theme.colors.textSecondary,
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      marginBottom: '10px',
    },
    description: {
      margin: '0 0 14px 0',
      color: theme.colors.textSecondary,
      fontSize: '12px',
      lineHeight: '1.45',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      flex: 1,
    },
    cardDivider: {
      height: '1px',
      backgroundColor: theme.colors.border,
      margin: '0 0 14px 0',
    },
    joinBtn: (kind) => {
      const palette = {
        live: {
          background: 'linear-gradient(90deg, #16a34a, #22c55e)',
          color: '#ffffff',
        },
        upcoming: {
          background: 'linear-gradient(90deg, #2563eb, #3b82f6)',
          color: '#ffffff',
        },
        disabled: {
          background: 'transparent',
          color: theme.colors.textTertiary,
          border: `1px solid ${theme.colors.border}`,
        },
      };
      const v = palette[kind] || palette.upcoming;
      return {
        width: '100%',
        borderRadius: '9px',
        border: v.border || 'none',
        padding: '10px 14px',
        fontSize: '14px',
        fontWeight: '700',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        cursor: kind === 'disabled' ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s',
        ...v,
      };
    },
    empty: {
      textAlign: 'center',
      color: theme.colors.textSecondary,
      padding: '48px 20px',
      border: `1px dashed ${theme.colors.border}`,
      borderRadius: '12px',
      backgroundColor: theme.colors.cardBg,
    },
    archiveSection: { marginTop: '8px' },
    archiveHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' },
    archiveTitle: {
      fontSize: '11px',
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: '0.8px',
      color: theme.colors.textTertiary,
    },
    tableWrap: {
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: 0,
      overflow: 'hidden',
    },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: {
      padding: '12px 16px',
      textAlign: 'left',
      fontSize: '11px',
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: theme.colors.textSecondary,
      backgroundColor: theme.colors.backgroundSecondary,
      borderBottom: `1px solid ${theme.colors.border}`,
      whiteSpace: 'nowrap',
    },
    td: {
      padding: '13px 16px',
      fontSize: '14px',
      color: theme.colors.text,
      borderBottom: `1px solid ${theme.colors.borderLight}`,
      verticalAlign: 'middle',
    },
    tdSub: { fontSize: '12px', color: theme.colors.textSecondary, marginTop: '2px' },
    archivePlayBtn: (enabled) => ({
      border: enabled ? `1px solid ${theme.colors.primary}` : `1px solid ${theme.colors.border}`,
      borderRadius: '8px',
      padding: '6px 10px',
      fontSize: '12px',
      fontWeight: '600',
      backgroundColor: enabled ? `${theme.colors.primary}12` : 'transparent',
      color: enabled ? theme.colors.primary : theme.colors.textTertiary,
      cursor: enabled ? 'pointer' : 'not-allowed',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
    }),
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.empty}>Loading meetings...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.4 } }
        .speaker-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(37, 99, 235, 0.12); }
        .speaker-join-btn:hover:not(:disabled) { filter: brightness(1.06); transform: translateY(-1px); }
        @media (max-width: 768px) {
          .speaker-grid { grid-template-columns: 1fr !important; }
          .speaker-archive-table th:nth-child(3) { display: none; }
          .speaker-archive-table td:nth-child(3) { display: none; }
        }
      `}</style>

      <div style={styles.searchWrap}>
        <Search size={16} style={styles.searchIcon} />
        <input
          style={styles.searchInput}
          type="text"
          placeholder="Search by title, description, or topic..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div style={styles.filtersGrid}>
        <input
          style={styles.filterInput}
          type="text"
          placeholder="Doctor"
          value={doctorFilter}
          onChange={(e) => setDoctorFilter(e.target.value)}
        />
        <input
          style={styles.filterInput}
          type="text"
          placeholder="Topic"
          value={topicFilter}
          onChange={(e) => setTopicFilter(e.target.value)}
        />
        <input
          style={styles.filterInput}
          type="date"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
        />
        <select
          style={styles.filterInput}
          value={languageFilter}
          onChange={(e) => setLanguageFilter(e.target.value)}
        >
          <option value="all">All Languages</option>
          {availableLanguages.map((language) => (
            <option key={language} value={language.toLowerCase()}>{language}</option>
          ))}
        </select>
      </div>

      {meetings.length === 0 ? (
        <div style={styles.empty}>No meetings scheduled yet.</div>
      ) : (
        <>
          <div style={styles.sectionLabel}>
            <Zap size={13} />
            My Assigned Sessions
            <span style={{ fontWeight: '400', color: theme.colors.textTertiary, textTransform: 'none', letterSpacing: 0 }}>
              ({upcoming.length})
            </span>
          </div>

          {upcoming.length === 0 ? (
            <div style={styles.empty}>No assigned upcoming sessions right now.</div>
          ) : (
            <div className="speaker-grid" style={styles.grid}>
              {upcoming.map((meeting) => {
                const assigned = isAssignedSpeaker(meeting);
                const isLive = meeting.status === 'ONGOING';
                const isCancelled = meeting.status === 'CANCELLED';
                const isJoining = joiningId === meeting.id;

                return (
                  <div
                    key={meeting.id}
                    className="speaker-card"
                    style={{
                      backgroundColor: theme.colors.cardBg,
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: '14px',
                      overflow: 'hidden',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <div
                      style={{
                        height: '4px',
                        background: isLive
                          ? 'linear-gradient(90deg, #16a34a, #22c55e)'
                          : 'linear-gradient(90deg, #2563eb, #3b82f6)',
                      }}
                    />

                    <div style={styles.cardBody}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <StatusPill status={meeting.status} />
                        {meeting.bannerUrl ? (
                          <img
                            src={meeting.bannerUrl}
                            alt=""
                            style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover' }}
                          />
                        ) : (
                          <Video size={18} color={theme.colors.textTertiary} />
                        )}
                      </div>

                      <span style={styles.badge(assigned)}>{assigned ? 'Assigned Speaker' : 'Assigned Viewer'}</span>
                      <h3 style={styles.meetingTitle}>{meeting.title}</h3>

                      <div style={styles.meta}>
                        <Calendar size={13} style={{ flexShrink: 0 }} />
                        {format(new Date(meeting.startTime), 'EEE, MMM d, yyyy')}
                      </div>
                      <div style={styles.meta}>
                        <Clock size={13} style={{ flexShrink: 0 }} />
                        {format(new Date(meeting.startTime), 'h:mm a')}
                      </div>
                      {meeting.speakers?.length > 0 && (
                        <div style={styles.meta}>
                          <Users size={13} style={{ flexShrink: 0 }} />
                          {getSpeakerNames(meeting)}
                        </div>
                      )}

                      {meeting.topic && <div style={styles.meta}>Topic: {meeting.topic}</div>}
                      {meeting.language && <div style={styles.meta}>Languages: {getLanguageList(meeting).join(', ')}</div>}

                      {meeting.description ? <p style={styles.description}>{meeting.description}</p> : <div style={{ flex: 1 }} />}

                      <div style={styles.cardDivider} />
                      <button
                        className="speaker-join-btn"
                        style={styles.joinBtn(isCancelled ? 'disabled' : isLive ? 'live' : 'upcoming')}
                        onClick={() => !isCancelled && handleJoinMeeting(meeting.id)}
                        disabled={isCancelled || isJoining}
                      >
                        {isJoining ? (
                          <span
                            style={{
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              border: '2px solid rgba(255,255,255,0.4)',
                              borderTopColor: '#fff',
                              animation: 'spin 0.7s linear infinite',
                              display: 'inline-block',
                            }}
                          />
                        ) : isLive ? (
                          <>
                            <PlayCircle size={16} />
                            Join Now
                          </>
                        ) : isCancelled ? (
                          'Cancelled'
                        ) : (
                          <>
                            <ChevronRight size={16} />
                            Join Meeting
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {allTableMeetings.length > 0 && (
            <div style={styles.archiveSection}>
              <div style={styles.archiveHeader}>
                <Archive size={13} style={{ color: theme.colors.textTertiary }} />
                <span style={styles.archiveTitle}>All Meetings</span>
                <span style={{ fontSize: '11px', color: theme.colors.textTertiary }}>({allTableMeetings.length})</span>
              </div>

              <div style={styles.tableWrap}>
                <table className="speaker-archive-table" style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Meeting</th>
                      <th style={styles.th}>Date</th>
                      <th style={styles.th}>Speakers</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>Recording</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allTableMeetings.map((meeting, i) => (
                      <tr
                        key={meeting.id}
                        style={{
                          backgroundColor: i % 2 === 0 ? 'transparent' : theme.colors.backgroundSecondary + '50',
                        }}
                      >
                        <td style={styles.td}>
                          <div style={{ fontWeight: '600', color: theme.colors.text }}>{meeting.title}</div>
                          {meeting.description && (
                            <div
                              style={{
                                ...styles.tdSub,
                                maxWidth: '280px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {meeting.description}
                            </div>
                          )}
                        </td>
                        <td style={styles.td}>
                          <div>{format(new Date(meeting.startTime), 'MMM d, yyyy')}</div>
                          <div style={styles.tdSub}>{format(new Date(meeting.startTime), 'h:mm a')}</div>
                        </td>
                        <td style={styles.td}>
                          {meeting.speakers?.length > 0 ? (
                            getSpeakerNames(meeting)
                          ) : (
                            <span style={{ color: theme.colors.textTertiary }}>-</span>
                          )}
                        </td>
                        <td style={styles.td}>
                          <StatusPill status={meeting.status} />
                        </td>
                        <td style={styles.td}>
                          <button
                            type="button"
                            style={styles.archivePlayBtn(Boolean(meeting.archiveRecordingUrl))}
                            onClick={() => handlePlayArchive(meeting)}
                            disabled={!meeting.archiveRecordingUrl}
                          >
                            <Play size={12} />
                            Play
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SpeakerMeetings;